import { useEffect, useRef } from "react";
import { useAccount } from "wagmi";
import type { Address } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/config";

// Custom event to trigger auth re-check
const AUTH_RECHECK_EVENT = "authRecheck";

/**
 * Dedicated hook to detect wallet switches and handle logout.
 * This should only be used once in the app to avoid multiple logout calls.
 */
export function useWalletSwitchDetector() {
    const { address } = useAccount();
    const queryClient = useQueryClient();
    const previousAddressRef = useRef<Address | undefined>();
    const isFirstRenderRef = useRef(true);

    useEffect(() => {
        const currentAddress = address;
        const previousAddress = previousAddressRef.current;

        // Skip wallet switch detection on the first render
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            previousAddressRef.current = currentAddress;
            return;
        }

        // If we have both a previous address and current address, and they're different
        // This indicates a wallet switch (not initial connection or disconnection)
        if (previousAddress && currentAddress && previousAddress !== currentAddress) {
            // Invalidate points data cache when wallet switches
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.POINTS.USER(currentAddress),
            });

            // Dispatch custom event to trigger auth re-check
            window.dispatchEvent(new CustomEvent(AUTH_RECHECK_EVENT));
        }

        // Update the ref with the current address
        previousAddressRef.current = currentAddress;
    }, [address, queryClient]);
}

// Export the event name for use in other hooks
export const AUTH_RECHECK_EVENT_NAME = AUTH_RECHECK_EVENT;
